import { useCallback, useId, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'
import Calendar from './Calendar.jsx'
import { addDays, daysInRange, formatDate, formatShort } from '../lib/dates.js'

/**
 * Every date a person picks in this app, picked the same way.
 *
 * This replaced four `<input type="date">` fields. A native date input renders as a different
 * control in every browser — Chrome's tri-segment spinner, Safari's, Firefox's — none of which
 * can be styled, so the one control that appeared on the board header, in the time-off modal
 * and in the mode-switch dialog was the only thing on screen that did not look like the rest
 * of the app, and looked different again depending on who was using it.
 *
 * The calendar itself is Calendar.jsx and the panel is Popover.jsx; what is left in this file
 * is the part that differs between the three places a date gets picked — one day, a from/to
 * pair, or a toolbar filter — which is mostly the question of what a click means.
 *
 * Everything here reads and writes 'YYYY-MM-DD'. There is no Date object anywhere in the
 * chain any more.
 */

/** The shared trigger: a calendar glyph and the date, or an invitation to choose one. */
function Trigger({ innerRef, id, open, disabled, className = '', label, text, onClick }) {
  return (
    <button
      type="button"
      id={id}
      ref={innerRef}
      className={`dp-trigger ${open ? 'open' : ''} ${className}`}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={label}
      onClick={onClick}
    >
      <Icon name="calendar" size={14} />
      <span className="dp-trigger-label">{text}</span>
    </button>
  )
}

/**
 * One date.
 *
 * `min`/`max` are enforced here as well as on the grid. The grid disables what it draws, and
 * this catches the Today button and anything a future caller wires straight through — a
 * cheap second gate on the one thing this control must not get wrong.
 */
export function DateField({
  value,
  onChange,
  min,
  max,
  weekStart = 1,
  today,
  disabled = false,
  id,
  label,
  className = '',
  withYear = true,
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)
  const close = useCallback(() => setOpen(false), [])

  const pick = (iso) => {
    if (!iso) return // clicking the selected day again would otherwise clear it
    if (min && iso < min) return
    if (max && iso > max) return
    onChange(iso)
    close()
  }

  return (
    <>
      <Trigger
        innerRef={anchorRef}
        id={id}
        open={open}
        disabled={disabled}
        className={className}
        // Without this the button's whole accessible name is the date it happens to show, so
        // a screen reader announces "Mon 24 Aug 2026, button" and never says what the date is
        // for. The native input this replaced got that from its <label>.
        label={label ? `${label}: ${value ? formatDate(value, { withYear }) : 'none chosen'}` : undefined}
        text={value ? formatDate(value, { withYear }) : 'Choose a date'}
        onClick={() => setOpen((o) => !o)}
      />

      <Popover open={open} anchorRef={anchorRef} close={close} className="dp-panel" label="Choose a date">
        <Calendar
          defaultMonth={value || today}
          selected={value || null}
          onPick={pick}
          min={min}
          max={max}
          today={today}
          weekStart={weekStart}
        />
        {today && (
          <div className="dp-footer">
            <button type="button" className="btn sm" onClick={() => pick(today)}>
              Today
            </button>
          </div>
        )}
      </Popover>
    </>
  )
}

/**
 * A run of days, as one field.
 *
 * This was two triggers, From and To, on the theory that naming both ends is clearer. In
 * practice a period of leave is one thing — "the 20th to the 24th" — and splitting it made
 * two controls that could disagree, each opening the same calendar to do half a job. One
 * field says what is booked, and the calendar behind it is where both ends get chosen.
 *
 * `presets` are the shortcuts a form wants offered — "This day", "This week" — listed down the
 * side of the calendar rather than beside the field. They belong with the thing they change: a
 * shortcut sitting outside the panel is a second control that quietly rewrites the first, with
 * nowhere for the calendar to show what it just did.
 */
export function DateRangeField({
  from,
  to,
  onChange,
  weekStart = 1,
  today,
  label = 'Dates',
  presets = [],
}) {
  const [open, setOpen] = useState(false)
  // Whether the first end of a NEW range has been clicked and the second is still to come.
  const [picking, setPicking] = useState(false)
  const anchorRef = useRef(null)
  const uid = useId()

  /**
   * One click is a complete answer: that day, on its own.
   *
   * The first click already writes both ends to the same day, so walking away after it leaves
   * a one-day period rather than a half-made one. Most absences are a single day, and making
   * the common case need a second click on the same square to confirm itself is a tax on it.
   */
  const cancel = useCallback(() => {
    setPicking(false)
    setOpen(false)
  }, [])

  const toggle = () => (open ? cancel() : setOpen(true))

  /**
   * Which two days the range runs between, decided from the day actually clicked.
   *
   * The obvious behaviour — stretch the existing range to meet the new click — is right for a
   * booking form where you are building a range up, and wrong here, where the dialog usually
   * opens on a period that already exists. Moving somebody's leave from next week to the week
   * after would silently produce a thirteen-day absence spanning both, and then close. So a
   * click on a settled range starts a new one.
   */
  const pick = (iso) => {
    if (!iso) return
    if (!picking) {
      // Both ends, so the day stands on its own if this is the last thing that happens.
      onChange(iso, iso)
      setPicking(true)
      return // held open — the second click has to have something to land on
    }
    // Clicking backwards is a normal way to pick a range, so the ends are ordered rather than
    // rejected.
    onChange(iso < from ? iso : from, iso < from ? from : iso)
    setPicking(false)
    setOpen(false)
  }

  /** A shortcut is a finished answer, so it also ends any pick in progress. */
  const applyPreset = (p) => {
    setPicking(false)
    onChange(p.from, p.to)
  }

  const last = to || from
  const days = daysInRange(from, last)
  // One day reads as the day. A run reads as both ends and how many, because "is that five
  // days or six" is the question anybody counting leave asks next.
  const text = !from
    ? 'Choose dates'
    : from === last
      ? formatDate(from, { withYear: true })
      : `${formatDate(from)} – ${formatDate(last, { withYear: true })}`

  return (
    <div className="field" ref={anchorRef}>
      <label htmlFor={uid}>{label}</label>
      <button
        type="button"
        id={uid}
        className={`dp-trigger dp-range-trigger ${open ? 'open' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon name="calendar" size={14} />
        <span className="dp-trigger-label">{text}</span>
        {days > 1 && <span className="dp-range-days">{days} days</span>}
      </button>

      <Popover open={open} anchorRef={anchorRef} close={cancel} className="dp-panel" label="Choose a date range">
        <div className="dp-body">
          {presets.length > 0 && (
            <div className="dp-presets">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className={`dp-preset ${from === p.from && last === p.to ? 'on' : ''}`}
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <Calendar
            mode="range"
            months={2}
            showOutside={false}
            // The month the range already sits in, or the day the dialog was opened about —
            // never simply this month, which on a dialog opened from a cell in March is a
            // calendar showing the wrong page.
            defaultMonth={from || today}
            selected={from ? { from, to: last } : null}
            onPick={pick}
            today={today}
            weekStart={weekStart}
          />
        </div>
        <div className="dp-footer">
          <span className="dp-range-summary">
            {picking
              ? `${formatShort(from)} — pick a last day, or click away for the one`
              : from
                ? `${formatShort(from)} – ${formatShort(last)} · ${days} ${days === 1 ? 'day' : 'days'}`
                : 'Pick the first day'}
          </span>
        </div>
      </Popover>
    </div>
  )
}

/**
 * A toolbar filter that is either off, on for one day, or on for a range.
 *
 * The same calendar as DateRangeField in a much smaller frame: a filter bar has no room for a
 * From/To pair of fields, and the question being asked — "who is away then" — is answered by
 * one click as often as by two. So one click sets a single day and closes nothing; a second
 * extends it into a range. Clicking again on a settled range starts over, for the same reason
 * DateRangeField does.
 */
export function DateRangeFilter({ label, value, onChange, weekStart = 1, today, title }) {
  const [open, setOpen] = useState(false)
  const [picking, setPicking] = useState(false)
  const anchorRef = useRef(null)
  const close = useCallback(() => { setOpen(false); setPicking(false) }, [])

  const from = value?.from || null
  const to = value?.to || from

  const pick = (iso) => {
    if (!iso) return
    if (!picking) {
      onChange({ from: iso, to: iso })
      setPicking(true)
      return // held open, so a second click can widen it without reopening
    }
    onChange(iso < from ? { from: iso, to: from } : { from, to: iso })
    close()
  }

  const clear = () => { onChange(null); close() }

  // One day reads as the day; a range reads as both ends. Neither repeats the label.
  const summary = !from ? null : from === to ? formatShort(from) : `${formatShort(from)} – ${formatShort(to)}`

  return (
    <>
      {/* Set, it is a chip with its own remove; unset, a plain button. Clearing a filter is
          the second most common thing done to one, and making it a trip back through the
          calendar is how a filter gets left on and quietly narrows everything after it. */}
      <span className={from ? 'dir-filter-chip' : undefined} ref={anchorRef}>
        <button
          type="button"
          className={`btn sm ${from ? 'primary' : ''}`}
          aria-haspopup="dialog"
          aria-expanded={open}
          title={title}
          onClick={() => { setPicking(false); setOpen((o) => !o) }}
        >
          {summary ? `${label} ${summary}` : `${label}…`}
        </button>
        {from && (
          <button
            type="button"
            className="btn sm primary dir-filter-clear"
            aria-label={`Clear the ${label.toLowerCase()} filter`}
            title={`Clear the ${label.toLowerCase()} filter`}
            onClick={clear}
          >
            <Icon name="close" size={13} />
          </button>
        )}
      </span>

      <Popover open={open} anchorRef={anchorRef} close={close} className="dp-panel" label={`${label} dates`}>
        <Calendar
          mode="range"
          showOutside={false}
          defaultMonth={from || today}
          selected={from ? { from, to } : null}
          onPick={pick}
          today={today}
          weekStart={weekStart}
        />
        <div className="dp-footer">
          <span className="dp-range-summary">
            {picking
              ? `From ${formatShort(from)} — pick a last day, or click away for the one`
              : from
                ? summary
                : 'Pick a day, or two for a range'}
          </span>
          {from && (
            <button type="button" className="btn ghost sm" onClick={clear}>
              Clear
            </button>
          )}
        </div>
      </Popover>
    </>
  )
}

/**
 * Back, Today, forward — the control every dated view in the app steers with.
 *
 * `step` is how much one press moves, which is the view's own business: a day on the day
 * board, seven on the week board, fourteen on the fortnight. Pulled out of Board so the wall's
 * keyboard navigation and the board's buttons cannot drift apart on what "next" means.
 *
 * `stepBy` overrides that arithmetic entirely, for the month view — months are not a number of
 * days, and stepping one by adding 30 lands you in the wrong one twice a year.
 */
export function DayNav({ date, setDate, today, step = 1, stepBy, labels = {} }) {
  const move = (dir) => setDate(stepBy ? stepBy(date, dir) : addDays(date, dir * step))
  return (
    <div className="segmented day-nav">
      <button type="button" onClick={() => move(-1)} aria-label={labels.prev || 'Previous'}>
        <Icon name="back" size={15} strokeWidth={2.2} />
      </button>
      <button type="button" onClick={() => setDate(today)} disabled={date === today}>
        Today
      </button>
      <button type="button" onClick={() => move(1)} aria-label={labels.next || 'Next'}>
        <Icon name="right" size={15} strokeWidth={2.2} />
      </button>
    </div>
  )
}
